<template>
  <div
    class="field"
    v-bind="$attrs"
  >
    <div class="control">
      <button
        class="button is-primary"
        type="button"
        v-on:click="openModal = allowImport"
        :disabled="!allowImport || undefined"
        :title="importButtonTitle"
      >
        Import
      </button>
    </div>
  </div>

  <Modal
    v-if="openModal"
    v-model="openModal"
    is-large
    title="Import a worker version"
  >
    <form
      class="form"
      id="worker-version-import-form"
      v-on:submit.prevent="submit"
    >
      <div class="field">
        <label class="label">Docker image reference</label>
        <div class="control">
          <input
            autofocus
            type="text"
            class="input"
            :class="{ 'is-danger': fieldErrors.dockerImageIID?.length }"
            required
            placeholder="[host[:port]/]namespace/repository[:tag]"
            maxlength="512"
            v-model.trim="dockerImageIID"
            :disabled="loading || undefined"
          />
        </div>
        <template v-if="fieldErrors.dockerImageIID">
          <p
            class="help is-danger"
            v-for="(error, i) in fieldErrors.dockerImageIID"
            :key="i"
          >
            {{ error }}
          </p>
        </template>
      </div>
      <div class="field">
        <label class="label">YAML configuration</label>
        <div class="control">
          <textarea
            class="textarea is-family-monospace"
            :class="{ 'is-danger': fieldErrors.yaml?.length }"
            v-model="yaml"
            required
            :disabled="loading || undefined"
          ></textarea>
        </div>
        <p class="help">
          This must conform to the
          <a
            href="https://doc.teklia.com/arkindex_worker_configuration/spec/"
            target="_blank"
            >Arkindex worker configuration specification</a
          >.
        </p>
        <template v-if="fieldErrors.yaml">
          <p
            class="help is-danger"
            v-for="(error, i) in fieldErrors.yaml"
            :key="i"
          >
            {{ error }}
          </p>
        </template>
      </div>
    </form>

    <template v-slot:footer="{ close }">
      <button
        type="button"
        class="button"
        v-on:click="close"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="worker-version-import-form"
        class="button is-primary"
        :class="{ 'is-loading': loading }"
        :title="submitButtonTitle"
        :disabled="!allowImport || loading || undefined"
      >
        Import
      </button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import Modal from '@/components/Modal.vue'
import { errorParser, isArkindexError } from '@/helpers'
import { useAuthStore, useNotificationStore, useWorkerStore } from '@/stores'
import type { UUID } from '@/types'
import type { Worker } from '@/types/worker'

const { workerId } = defineProps<{ workerId: UUID }>()
const emit = defineEmits<{ created: [] }>()

const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const workerStore = useWorkerStore()

const openModal = ref(false)
const loading = ref(false)

const dockerImageIID = ref('')
const yaml = ref('')
const fieldErrors = ref<{ dockerImageIID: string[]; yaml: string[] }>({
  dockerImageIID: [],
  yaml: [],
})

const worker = computed<Worker | null>(() => {
  return workerStore.workers[workerId] ?? null
})
/**
 * Whether any worker version import can be made at all.
 */
const allowImport = computed<boolean>(() => {
  if (!authStore.user || !worker.value) return false
  return (
    !worker.value.repository_url &&
    !worker.value.archived &&
    (authStore.user.is_admin || authStore.user.can_create_worker_version)
  )
})
/**
 * `title` of the button that opens the import modal, before anything can be typed into a form
 */
const importButtonTitle = computed<string>(() => {
  if (loading.value || !worker.value) return 'Loading…'
  if (!authStore.user || (!authStore.user.is_admin && !authStore.user.can_create_worker_version))
    return 'You do not have the required permission to import worker versions'
  if (worker.value.archived) return 'This worker is archived'
  if (worker.value.repository_url)
    return 'Worker versions cannot be imported from the web interface on workers with a repository URL'
  return 'Import a new worker version'
})
/**
 * `title` of the button that submits the form inside the modal.
 */
const submitButtonTitle = computed<string>(() => {
  if (!allowImport.value || loading.value) return importButtonTitle.value
  if (!dockerImageIID.value.length) return 'A Docker image reference is required'
  if (!yaml.value.length) return 'A YAML configuration is required'
  return 'Import this worker version'
})

async function submit() {
  if (!allowImport.value || loading.value || !dockerImageIID.value || !yaml.value) return
  loading.value = true
  fieldErrors.value = { dockerImageIID: [], yaml: [] }
  try {
    await workerStore.importWorkerVersion(workerId, {
      docker_image_iid: dockerImageIID.value,
      yaml: yaml.value,
    })
    notificationStore.notify({ type: 'info', text: 'Worker version imported successfully.' })
    emit('created')
    openModal.value = false
    dockerImageIID.value = ''
    yaml.value = ''
  } catch (err) {
    notificationStore.notify({ type: 'error', text: errorParser(err) })
    if (isArkindexError(err)) {
      for (const error of err.response.data.errors) {
        if (!error.attr) continue
        if (error.attr[0] === 'docker_image_iid')
          fieldErrors.value.dockerImageIID.push(error.detail)
        else if (error.attr[0] === 'yaml') fieldErrors.value.yaml.push(error.detail)
      }
    }
  } finally {
    loading.value = false
  }
}
</script>
